/**
 * Ingest helpers — pure, verbatim from source (mcpTools / ingestResult tiers).
 * The wizard state machine itself lives in the store reducer (it has timers).
 */
import type { Adapter, Tier } from './types';
import { COLORS, TIERS } from '../data/seeds';
import { detectCategory, initialsOf } from './naming';

export type McpTool = { name: string; kind: string; isNew?: boolean };

const MCP_TOOLS_BY_CAT: Record<string, [string, string][]> = {
  CRM: [['search_contacts', 'query'], ['create_contact', 'mutation'], ['update_deal', 'mutation'], ['list_pipelines', 'query']],
  Scheduling: [['list_availability', 'query'], ['create_booking', 'mutation'], ['cancel_booking', 'mutation'], ['get_calendar', 'query']],
  Messaging: [['send_message', 'mutation'], ['list_channels', 'query'], ['create_thread', 'mutation']],
  Email: [['send_email', 'mutation'], ['search_inbox', 'query'], ['create_draft', 'mutation']],
  Payments: [['create_charge', 'mutation'], ['list_invoices', 'query'], ['refund_payment', 'mutation']],
  Support: [['create_ticket', 'mutation'], ['search_tickets', 'query'], ['update_ticket', 'mutation']],
};

/** MCP tools for a category (+ bulk_export when resynced/extra). */
export function mcpToolsFor(cat: string, extra: boolean): McpTool[] {
  const tools = (MCP_TOOLS_BY_CAT[cat] || MCP_TOOLS_BY_CAT.CRM).map(t => ({
    name: t[0],
    kind: t[1],
    isNew: false,
  }));
  if (extra) tools.push({ name: 'bulk_export', kind: 'query', isNew: true });
  return tools;
}

export type IngestResult = {
  adapter: Adapter;
  conf: string;
  edges: string;
  needs: number;
  auth: string;
  change: Tier['change'];
  classLabel: string;
};

/** Build the drafted-adapter result from an ingest run. */
export function ingestResult(
  sourceType: 'openapi' | 'prose',
  url: string,
  detectedName: string | undefined,
  category: string | undefined,
  adaptersLen: number,
): IngestResult {
  const T = TIERS[sourceType];
  const name = detectedName || 'New vendor';
  const initials = initialsOf(name) || 'NV';
  const color = COLORS[adaptersLen % COLORS.length];
  return {
    adapter: {
      id: 'adp-' + Date.now(),
      name,
      initials,
      color,
      cls: T.cls,
      source: T.source,
      version: '1.0',
      purpose:
        'Newly ingested ' + T.cls.toUpperCase() + ' adapter — review the draft mapping before going live.',
      products: [{ key: 'CRM', dataType: 'contacts' }, { key: 'Hire', dataType: 'contacts' }],
      change: T.change,
      company: name,
      category: category || detectCategory(url),
    },
    conf: T.conf,
    edges: T.edges,
    needs: T.needs,
    auth: T.auth,
    change: T.change,
    classLabel: T.cls.toUpperCase(),
  };
}
